import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Bot,
  CandlestickChart,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  FolderKanban,
  History,
  LayoutDashboard,
  Link2,
  LogOut,
  Megaphone,
  Network,
  Shield,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";
import { clearClientSession } from "../services/api";
import { getCurrentUser } from "../services/sessionCache";
import "./NavBar.css";

const navSections = [
  {
    label: "Overview",
    items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Trades",
    items: [
      { to: "/active-trades", label: "Active Trades", icon: CandlestickChart },
      { to: "/pending-orders", label: "Pending Trades", icon: Clock },
      { to: "/closed-trades", label: "Closed Trades", icon: History },
    ],
  },
  {
    label: "Tools",
    items: [{ to: "/trade-execution", label: "Trade Execution", icon: Zap }],
  },
  {
    label: "Account",
    items: [
      { to: "/link-account", label: "Link Account", icon: Link2 },
      { to: "/telegram-hub", label: "Telegram Hub", icon: Bot },
    ],
  },
  {
    label: "Affiliate",
    items: [{ to: "/network", label: "My Network", icon: Network }],
  },
];

function BrandMark() {
  return <TrendingUp className="sidebar-brand-icon" />;
}

export default function NavBar({
  mobileOpen = false,
  onMobileClose,
  collapsed = false,
  onToggleCollapse,
  isMobile = false,
}) {
  const location = useLocation();
  const [role, setRole] = useState("");

  useEffect(() => {
    const PUBLIC = ["/login", "/signup", "/forgot-password", "/reset-password"];
    if (PUBLIC.includes(location.pathname)) {
      setRole("");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setRole("");
      return;
    }

    getCurrentUser()
      .then((user) => setRole(user?.role || ""))
      .catch(() => setRole(""));
  }, [location]);

  const adminSection = {
    label: "Admin",
    items: [
      { to: "/admin/overview", label: "Overview", icon: Shield },
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/accounts", label: "Accounts", icon: FolderKanban },
      { to: "/admin/broadcast", label: "Broadcast", icon: Megaphone },
    ],
  };

  const closeMobileIfNeeded = () => {
    if (isMobile && typeof onMobileClose === "function") {
      onMobileClose();
    }
  };

  const handleLogout = () => {
    closeMobileIfNeeded();
    clearClientSession("/login");
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <button
          className="sidebar-overlay"
          onClick={closeMobileIfNeeded}
          aria-label="Close sidebar"
        />
      )}

      <aside
        className={[
          "sidebar",
          collapsed ? "sidebar-collapsed" : "",
          isMobile ? "sidebar-mobile" : "",
          mobileOpen ? "sidebar-mobile-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="sidebar-header">
          <div className="sidebar-header-row">
            <div className="sidebar-brand">
              <BrandMark />
            </div>

            {isMobile ? (
              <button
                className="sidebar-toggle-btn"
                onClick={closeMobileIfNeeded}
                aria-label="Close sidebar"
              >
                <X size={14} />
              </button>
            ) : (
              <button
                className="sidebar-toggle-btn"
                onClick={onToggleCollapse}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
              </button>
            )}
          </div>
        </div>

        <nav className="sidebar-nav">
          {navSections.map(({ label: sectionLabel, items }) => (
            <div key={sectionLabel} className="sidebar-section">
              <span className="sidebar-section-label">{sectionLabel}</span>
              {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/dashboard"}
                  className={({ isActive }) => `sidebar-link${isActive ? " active" : ""}`}
                  onClick={closeMobileIfNeeded}
                  title={collapsed ? label : undefined}
                >
                  <span className="sidebar-link-icon">
                    <Icon size={16} />
                  </span>
                  <span className="sidebar-link-label">{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
          {role === "admin" && (
            <div className="sidebar-section sidebar-section-admin">
              <span className="sidebar-section-label">{adminSection.label}</span>
              {adminSection.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => `sidebar-link${isActive ? " active" : ""}`}
                  onClick={closeMobileIfNeeded}
                  title={collapsed ? label : undefined}
                >
                  <span className="sidebar-link-icon">
                    <Icon size={16} />
                  </span>
                  <span className="sidebar-link-label">{label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-link sidebar-link-footer sidebar-link-button"
            onClick={handleLogout}
            title={collapsed ? "Logout" : undefined}
          >
            <span className="sidebar-link-icon">
              <LogOut size={16} />
            </span>
            <span className="sidebar-link-label">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}
